import * as THREE from "three";
import { createPointerHandlers } from "./createPointerHandlers";

/**
 * 📦 bindPointerRaycast
 * - Pointer → NDC 변환 → Raycast → Hover/Click 처리를 전체적으로 관리하는 모듈
 * - createPointerHandlers 로 실제 이벤트에 따른 반응(onEnter/onLeave/onClick 등)을 위임
 */
export function bindPointerRaycast({
  mount,
  camera,
  getTargets,
  meshRef,
  options = {},
  setIsMarked,
}) {
  if (!mount || !camera || !getTargets) return () => {};

  const { throttleWithRaf = true, pointsThreshold = 0.01 } = options;

  // ===============================================================
  // STEP 1 : 기본 객체 및 초기 상태 생성
  // ===============================================================
  const raycaster = new THREE.Raycaster();
  raycaster.params.Points.threshold = pointsThreshold;

  const pointerNDC = new THREE.Vector2();
  let hovered = null; // 현재 Hover 중인 Intersect 정보
  let rafId = 0;
  let pending = false;
  let lastEvent = null;

  // ==================== 클릭 vs 드래그 구분용 ====================
  let downX = 0,
    downY = 0,
    moved = false;

  // ===============================================================
  // STEP 2 : Pointer → NDC 변환 함수 px 단위 좌표를 NDC -1 ~ +1 범위로 변환
  // ===============================================================
  const computeNDC = (e) => {
    const r = mount.getBoundingClientRect();
    pointerNDC.set(
      ((e.clientX - r.left) / r.width) * 2 - 1,
      -(((e.clientY - r.top) / r.height) * 2 - 1)
    );
  };

  // ===============================================================
  // STEP 3 : Pointer 이벤트 핸들러 Factory 생성
  // (실제 onEnter / onLeave / onMove / onClick 로직은 내부에서 처리)
  // ===============================================================
  const {
    onEnter,
    onMove,
    onLeave,
    onClick,
    reset,
    dispose,
    onPointerDown,
    onPointerUp,
  } = createPointerHandlers({
    mount,
    camera,
    getTargets,
    meshRef,
    highlightColor: 0x222222,
    setIsMarked,
    getMesh: () => meshRef.current,
  });

  // ===============================================================
  // STEP 4 : Raycast 실행 메인 루틴
  // - pointermove 시점에 NDC → Raycast → Enter/Move/Leave 로직 실행
  // ===============================================================
  const cast = () => {
    pending = false;
    if (!lastEvent) return;

    // STEP 4-1 : 좌표계 변환
    computeNDC(lastEvent);

    // STEP 4-2 : 타겟 오브젝트 조회
    const targets = getTargets() || [];
    if (!targets.length) {
      if (hovered) {
        onLeave(hovered);
        hovered = null;
      }
      return;
    }

    // STEP 4-3 : Raycast 실행
    raycaster.setFromCamera(pointerNDC, camera);
    const hit = raycaster.intersectObjects(targets, true)[0] || null;

    const prev = hovered;

    // STEP 4-4 : Hover 갱신(enter/leave)
    if (hit && (!prev || prev.object !== hit.object)) {
      if (prev) onLeave(prev);
      onEnter(hit);
      hovered = hit;
    }

    // STEP 4-5 : Move 처리
    if (hit) onMove(hit);

    // STEP 4-6 : Hit 없음 → leave 처리
    if (!hit && prev) {
      onLeave(prev);
      hovered = null;
    }
  };

  // ===============================================================
  // STEP 5 : Pointer 이벤트 바인딩
  // ===============================================================

  // STEP 5-1 : PointerDown (드래그/클릭 구분 시작점)
  const handlePointerDown = (e) => {
    downX = e.clientX;
    downY = e.clientY;
    moved = false;
    onPointerDown(e);
  };

  // STEP 5-2 : PointerMove (Raycast 실행)
  const handlePointerMove = (e) => {
    // STEP 5-2-1 : 드래그 감지
    if (Math.abs(e.clientX - downX) > 5 || Math.abs(e.clientY - downY) > 5) {
      moved = true;
    }

    // STEP 5-2-2 : Raycast 예약
    lastEvent = e;
    if (!throttleWithRaf) {
      cast();
    } else {
      if (pending) return;
      pending = true;
      rafId = requestAnimationFrame(cast);
    }
  };

  // STEP 5-3 : PointerUp
  const handlePointerUp = (e) => {
    onPointerUp(e);
  };

  // STEP 5-4 : Click (드래그 없음 → 클릭 인정)
  const handleClick = (e) => {
    if (!moved) onClick(e);
  };

  // STEP 5-5 : PointerLeave (hover 초기화)
  const handlePointerLeave = () => {
    if (hovered) {
      onLeave(hovered);
      hovered = null;
    }
  };

  // ======= 실제 DOM 바인딩 =======
  mount.addEventListener("pointerdown", handlePointerDown, { passive: true });
  mount.addEventListener("pointermove", handlePointerMove, { passive: true });
  mount.addEventListener("pointerup", handlePointerUp, { passive: true });
  mount.addEventListener("pointerleave", handlePointerLeave, { passive: true });
  mount.addEventListener("click", handleClick, { passive: true });

  // ===============================================================
  // STEP 6 : Cleanup 반환
  // ===============================================================
  return () => {
    mount.removeEventListener("pointerdown", handlePointerDown);
    mount.removeEventListener("pointermove", handlePointerMove);
    mount.removeEventListener("pointerup", handlePointerUp);
    mount.removeEventListener("pointerleave", handlePointerLeave);
    mount.removeEventListener("click", handleClick);

    if (rafId) cancelAnimationFrame(rafId);
    if (hovered) reset(hovered);

    dispose?.();
  };
}
